import { useEffect } from 'react';
import { useRedactionMode } from '../contexts/RedactionModeContext';

interface PatientData {
  name?: string;
  identifier?: string;
  identifierType?: string;
  dateOfBirth?: string;
}

interface CaseData {
  patientName?: string;
  name?: string;
}

// Full-name placeholders that should never be registered as PII
const PLACEHOLDER_NAMES = new Set([
  'anonymous patient', 'unknown', 'anonymous', 'not provided', 'n/a',
]);

// Common words that should not be registered as individual name parts
const COMMON_WORDS = new Set([
  'patient', 'anonymous', 'unknown', 'doctor', 'consultant', 'nurse',
  'hospital', 'clinic', 'medical', 'health', 'care', 'service',
  'mr', 'mrs', 'ms', 'miss', 'dr', 'prof',
]);

/**
 * Registers structured PII (patient names, DOBs, identifiers) from case data
 * so that Tier 1 blurring can apply immediately without waiting for a Haiku call.
 *
 * Call this in CaseDetailPage or any view that has access to case/patient data.
 */
export function useStructuredPiiRegistration(
  caseData: CaseData | undefined | null,
  patients?: PatientData[]
) {
  const { isEnabled, replaceStructuredPii, clearStructuredPii } = useRedactionMode();

  useEffect(() => {
    if (!isEnabled) return;

    const piiValues: string[] = [];

    // Case-level patient name — skip placeholders like "Anonymous Patient"
    if (caseData?.patientName && !PLACEHOLDER_NAMES.has(caseData.patientName.toLowerCase().trim())) {
      piiValues.push(caseData.patientName);
      // Also register individual name parts (first name, surname), filtering common words
      caseData.patientName.split(/\s+/).forEach(part => {
        if (part.length > 2 && !COMMON_WORDS.has(part.toLowerCase())) {
          piiValues.push(part);
        }
      });
    }

    // Detected/confirmed patients
    if (patients) {
      for (const p of patients) {
        if (p.name && !PLACEHOLDER_NAMES.has(p.name.toLowerCase().trim())) {
          piiValues.push(p.name);
          p.name.split(/\s+/).forEach(part => {
            if (part.length > 2 && !COMMON_WORDS.has(part.toLowerCase())) {
              piiValues.push(part);
            }
          });
        }
        if (p.identifier) piiValues.push(p.identifier);
        if (p.dateOfBirth) piiValues.push(p.dateOfBirth);
      }
    }

    // Atomically replace the entire structured PII set
    replaceStructuredPii(piiValues.filter(Boolean));
  }, [isEnabled, caseData, patients, replaceStructuredPii]);

  // Clear structured PII when the component unmounts (navigating away from case)
  useEffect(() => {
    return () => {
      clearStructuredPii();
    };
  }, [clearStructuredPii]);
}
